import { components, paths } from "@minuthq/minut-api-types/schema"
import { useMutation, useQuery, UseQueryOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { API_DEFAULT } from "src/constants/minutApi"
import {
  guestKeys,
  useGuestCache,
} from "src/data/guests/queries/guestQueryCache"
import {
  TFetchHomeGuestsResponse,
  TGuest,
  TPostGuestRequestBody,
  TPutGuestRequestBody,
} from "src/data/guests/types/guestTypes"
import { useMinutQuery } from "src/hooks/minutQuery"
import { sleep } from "src/utils/genericUtil"
import { minutApiHttpClient } from "src/utils/minutApiHttpClient"

export enum SortOrder {
  ASCENDING = "asc",
  DESCENDING = "desc",
}
export enum SortKey {
  CHECK_IN = "checkinTime",
  CHECK_OUt = "checkoutTime",
}

export async function deleteGuest(
  homeId: string,
  guestId: string
): Promise<TGuest> {
  const response = await minutApiHttpClient.delete(
    `${API_DEFAULT}/homes/${homeId}/guests/${guestId}`
  )
  const home: TGuest = response.data
  return home
}

export async function fetchGuests(
  homeId: string,
  limit?: number,
  offset?: number,
  checkin_filter?: {
    from?: Date
    to?: Date
  },
  checkout_filter?: {
    from?: Date
    to?: Date
  },
  sort_options?: {
    order?: SortOrder
    key: SortKey
  }
): Promise<TFetchHomeGuestsResponse> {
  let checkinFilter: { from?: string; to?: string } | undefined = undefined
  let checkOutfilter: { from?: string; to?: string } | undefined = undefined
  if (checkin_filter) {
    checkinFilter = {
      from: checkin_filter.from?.toISOString(),
      to: checkin_filter.to?.toISOString(),
    }
  }
  if (checkout_filter) {
    checkOutfilter = {
      from: checkout_filter.from?.toISOString(),
      to: checkout_filter.to?.toISOString(),
    }
  }
  const response = await minutApiHttpClient.get(
    `${API_DEFAULT}/homes/${homeId}/guests`,
    {
      params: {
        limit,
        offset,
        checkin_filter: checkinFilter,
        checkout_filter: checkOutfilter,
        sort_options,
      },
    }
  )

  return {
    guests: response.data?.guests || [],
    paging: response.data?.paging,
  }
}

export function useFetchHomeGuests(
  homeId: string,
  props?: {
    limit?: number
    offset?: number
    checkin_filter?: {
      from?: Date
      to?: Date
    }
    checkout_filter?: {
      from?: Date
      to?: Date
    }
    sort_options?: {
      order?: SortOrder
      key: SortKey
    }
    options?: UseQueryOptions<
      TFetchHomeGuestsResponse,
      AxiosError,
      TFetchHomeGuestsResponse,
      ReturnType<typeof guestKeys.homeList>
    >
  }
) {
  const {
    limit,
    offset,
    checkin_filter,
    checkout_filter,
    sort_options,
    options,
  } = props || {}
  return useQuery(
    guestKeys.homeList(homeId, {
      limit,
      offset,
      checkin_filter,
      checkout_filter,
      sort_options,
    }),
    () => {
      // TODO WEB-321  Use correct query key
      return fetchGuests(
        homeId,
        limit,
        offset,
        checkin_filter,
        checkout_filter,
        sort_options
      )
    },
    {
      keepPreviousData: true,
      ...options,
    }
  )
}

export function useDeleteGuest({ homeId }: { homeId: string }) {
  const { invalidateGuestList } = useGuestCache()

  return useMutation((guestId: string) => deleteGuest(homeId, guestId), {
    onSuccess: () => {
      invalidateGuestList(homeId)
    },
  })
}

export function usePostGuest() {
  const { invalidateGuestList } = useGuestCache()

  async function postGuest(homeId: string, guest: TPostGuestRequestBody) {
    const response = await minutApiHttpClient.post<TGuest>(
      `${API_DEFAULT}/homes/${homeId}/guests`,
      guest
    )
    return response.data
  }

  return useMutation(
    ({ homeId, guest }: { homeId: string; guest: TPostGuestRequestBody }) =>
      postGuest(homeId, guest),
    {
      onSuccess: (_data, { homeId }) => {
        invalidateGuestList(homeId)
      },
    }
  )
}

export function usePutGuest() {
  const { invalidateGuestList } = useGuestCache()

  async function putGuest({
    homeId,
    guestId,
    guest,
  }: {
    homeId: string
    guestId: string
    guest: TPutGuestRequestBody
  }) {
    const response = await minutApiHttpClient.put<TGuest>(
      `${API_DEFAULT}/homes/${homeId}/guests/${guestId}`,
      guest
    )
    return response.data
  }

  return useMutation(putGuest, {
    onSuccess: (_data, { homeId }) => {
      invalidateGuestList(homeId)
    },
  })
}

export function useFetchOrganizationGuests({
  orgId,
  options,
}: {
  orgId: string
  options?: {
    refreshInterval?: number
  }
}) {
  return useMinutQuery<"/organizations/{organization_id}/guests">({
    queryKey: guestKeys.organizationList(orgId),
    apiPath: "/organizations/{organization_id}/guests",
    queryPath: `/organizations/${orgId}/guests`,
    options: {
      refetchInterval: options?.refreshInterval,
    },
  })
}

export function useFetchGuestMessages({
  orgId,
  guestId,
}: {
  orgId: string
  guestId: string
  options?: UseQueryOptions<
    paths["/organizations/{organization_id}/guests/{guest_id}/messages"]["get"]["responses"]["200"]["content"]["application/json"],
    AxiosError,
    paths["/organizations/{organization_id}/guests/{guest_id}/messages"]["get"]["responses"]["200"]["content"]["application/json"],
    ReturnType<typeof guestKeys.organizationGuestMessages>
  >
}) {
  async function fetchGuestMessages(): Promise<
    paths["/organizations/{organization_id}/guests/{guest_id}/messages"]["get"]["responses"]["200"]["content"]["application/json"]
  > {
    await sleep(500)
    return {
      messages: [...mockedMessages],
      paging: {
        total_count: mockedMessages.length,
        offset: 0,
        limit: 10,
      },
    }
  }

  return useQuery({
    queryKey: guestKeys.organizationGuestMessages(orgId, guestId),
    queryFn: fetchGuestMessages,
  })
}

export function usePostGuestMessage() {
  const { invalidateGuestMessages } = useGuestCache()

  async function postGuestMessage({
    orgId,
    guestId,
    message,
    communicationChannel,
    senderName,
    senderType,
  }: {
    orgId: string
    guestId: string
    message: string
    communicationChannel: "sms" | "whatsapp"
    senderName: string
    senderType: "user" | "guest"
  }) {
    await sleep(500)

    mockedMessages.push({
      id: (mockedMessages.length + 1).toString(),
      guest_id: guestId,
      home_id: "1",
      organization_id: orgId,
      communication_channel: communicationChannel,
      sender_name: senderName,
      sender_type: senderType,
      content: message,
      timestamp: new Date().toISOString(),
      read: false,
    })
  }

  return useMutation({
    mutationFn: postGuestMessage,
    onSuccess: (_data, { orgId, guestId }) => {
      invalidateGuestMessages(orgId, guestId)
    },
  })
}

const mockedMessages: components["schemas"]["GuestMessage"][] = [
  {
    id: "1",
    guest_id: "1",
    home_id: "1",
    organization_id: "1",
    communication_channel: "sms",
    sender_name: "Sarah Wilson",
    sender_type: "guest",
    content:
      "Hi! I just arrived at the apartment. The code you gave me isn't working - could you help?",
    timestamp: "2024-01-15T14:23:00Z",
    read: false,
  },
  {
    id: "2",
    guest_id: "1",
    home_id: "1",
    organization_id: "1",
    communication_channel: "sms",
    sender_name: "Minut AI",
    sender_type: "minut_ai",
    content:
      "I'll notify the host right away about the door code issue. In the meantime, please make sure you're entering it with the # symbol at the end.",
    timestamp: "2024-01-15T14:23:30Z",
    read: false,
  },
  {
    id: "3",
    guest_id: "1",
    home_id: "1",
    organization_id: "1",
    communication_channel: "sms",
    sender_name: "Host",
    sender_type: "user",
    content:
      "Hi Sarah! So sorry about that. The code is 1234#. Let me know if you still have trouble getting in.",
    timestamp: "2024-01-15T14:25:00Z",
    read: false,
  },
  {
    id: "4",
    guest_id: "1",
    home_id: "1",
    organization_id: "1",
    communication_channel: "sms",
    sender_name: "Sarah Wilson",
    sender_type: "guest",
    content:
      "Perfect, I'm in now! The apartment looks lovely. Quick question - where can I find extra towels?",
    timestamp: "2024-01-15T14:28:00Z",
    read: false,
  },
  {
    id: "5",
    guest_id: "1",
    home_id: "1",
    organization_id: "1",
    communication_channel: "sms",
    sender_name: "Minut AI",
    sender_type: "minut_ai",
    content:
      "Extra towels can be found in the hallway closet on the top shelf. You'll also find additional bed linens there if needed.",
    timestamp: "2024-01-15T14:28:30Z",
    read: false,
  },
  {
    id: "6",
    guest_id: "1",
    home_id: "1",
    organization_id: "1",
    communication_channel: "sms",
    sender_name: "Sarah Wilson",
    sender_type: "guest",
    content: "Found them, thanks! Looking forward to my stay.",
    timestamp: "2024-01-15T14:30:00Z",
    read: false,
  },
  {
    id: "7",
    guest_id: "1",
    home_id: "1",
    organization_id: "1",
    communication_channel: "sms",
    sender_name: "Host",
    sender_type: "user",
    content:
      "Hi Sarah! Just wanted to share some additional information about the apartment and area:\n\nWiFi Network: AptGuest123\nPassword: Welcome2024!\n\nSome local recommendations:\n- Joe's Cafe around the corner has amazing breakfast\n- The park two blocks north is perfect for morning walks\n- There's a great grocery store called Martin's Market within walking distance\n\nA few house rules to keep in mind:\n1. Please remove shoes when entering\n2. Quiet hours are from 10pm to 7am\n3. Recycling bins are color coded - blue for paper, green for glass\n\nDon't hesitate to reach out if you need anything else during your stay! We want to make sure you have a wonderful experience.",
    timestamp: "2024-01-15T14:35:00Z",
    read: false,
  },
  {
    id: "8",
    guest_id: "1",
    home_id: "1",
    organization_id: "1",
    communication_channel: "sms",
    sender_name: "Sarah Wilson",
    sender_type: "guest",
    content:
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.",
    timestamp: "2024-01-15T14:40:00Z",
    read: false,
  },
]
